import type {
  ProductDetail,
  ProductFilters,
  ProductListPage,
  ProductListQuery,
} from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import type { ProductService } from './ProductService';
import { productDetailDto, productFiltersDto, productListDto } from './product.dto';
import { toProductDetail, toProductFilters, toProductListPage } from './product.mapper';

export function createLaravelProductService(transport: Transport): ProductService {
  return {
    async list(query: ProductListQuery, signal): Promise<ProductListPage> {
      const attributeQuery = Object.fromEntries(
        Object.entries(query.attributeFilters ?? {})
          .filter(([, values]) => values.length > 0)
          .map(([code, values]) => [
            code === 'category' ? 'attribute_category' : code,
            values.join(','),
          ]),
      );
      const res = await transport.request({
        method: 'GET',
        path: '/products',
        query: {
          page: query.page ?? 1,
          per_page: query.pageSize ?? 24,
          category: query.categorySlug ?? null,
          brand: query.brandOptionId ?? null,
          search: query.search ?? null,
          sort: query.sort ?? null,
          popular: query.popular ? 1 : null,
          special_price: query.specialPrice ? 1 : null,
          new: query.isNew ? 1 : null,
          in_stock: query.inStock ? 1 : null,
          price_min: query.priceMin ?? null,
          price_max: query.priceMax ?? null,
          ...attributeQuery,
        },
        ...(signal !== undefined ? { signal } : {}),
      });
      return toProductListPage(parseDto(productListDto, res.data, 'GET /products'));
    },

    async filters(query, signal): Promise<ProductFilters> {
      const res = await transport.request({
        method: 'GET',
        path: '/products/filters',
        query: { category: query.categorySlug ?? null, search: query.search ?? null },
        ...(signal !== undefined ? { signal } : {}),
      });
      return toProductFilters(parseDto(productFiltersDto, res.data, 'GET /products/filters'));
    },

    async getBySlug(slug: string, signal): Promise<ProductDetail> {
      const res = await transport.request({
        method: 'GET',
        path: `/products/${encodeURIComponent(slug)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      return toProductDetail(parseDto(productDetailDto, res.data, `GET /products/${slug}`));
    },
  };
}
